// The three controls a filter row is made of.
//
// `FilterSelect` and `MonthStepper` are the blocks /billing and /billing/close
// each declared for themselves; `SelectionChips` is new, and it is a
// multi-select that shows every option at once — which is right for a set of
// four deployments and wrong for a list of two hundred partners.

import type { ReactNode } from "react";
import { Button } from "./Button.js";
import styles from "./Filters.module.css";

export interface FilterOption<T extends string> {
  readonly value: T;
  readonly label: string;
}

export interface FilterSelectProps<T extends string> {
  /** The word before the control, e.g. "Break down by". */
  readonly label: string;
  readonly options: ReadonlyArray<FilterOption<T>>;
  readonly value: T;
  readonly onChange: (value: T) => void;
}

/**
 * A labelled `<select>`.
 *
 * `onChange` is handed a value from `options`, never the raw string: the caller
 * declares the type, and a value the list does not hold is dropped here rather
 * than parsed again by every caller.
 */
export function FilterSelect<T extends string>({
  label,
  options,
  value,
  onChange,
}: FilterSelectProps<T>): ReactNode {
  return (
    <label className={styles.filter}>
      <span className={styles.filterLabel}>{label}:</span>
      <select
        className={styles.select}
        value={value}
        onChange={(event) => {
          const chosen = options.find((option) => option.value === event.target.value);
          if (chosen !== undefined) onChange(chosen.value);
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export interface MonthStepperProps {
  /** What the month reads as, e.g. "September 2026". Formatting is the caller's. */
  readonly label: string;
  readonly onStep: (delta: number) => void;
}

/** `‹ September 2026 ›`. One month a step, and back walks the months. */
export function MonthStepper({ label, onStep }: MonthStepperProps): ReactNode {
  return (
    <div className={styles.monthPick}>
      <Button variant="secondary" onClick={() => onStep(-1)} aria-label="Previous month">
        ‹
      </Button>
      <span className={styles.monthLabel}>{label}</span>
      <Button variant="secondary" onClick={() => onStep(1)} aria-label="Next month">
        ›
      </Button>
    </div>
  );
}

export interface SelectionChip {
  readonly value: string;
  readonly label: string;
  /** Drawn dashed and never on by default. A nonprod deployment is the case. */
  readonly cautioned?: boolean;
}

export interface SelectionChipsProps {
  readonly label: string;
  readonly options: readonly SelectionChip[];
  readonly selected: readonly string[];
  readonly onToggle: (value: string) => void;
}

/**
 * A row of chips, one click to a chip.
 *
 * Buttons with `aria-pressed`, not checkboxes: each one is an action that
 * redraws the figures, and a screen reader should announce it as pressed rather
 * than as a form field waiting to be submitted.
 */
export function SelectionChips({
  label,
  options,
  selected,
  onToggle,
}: SelectionChipsProps): ReactNode {
  if (options.length === 0) return null;
  return (
    <div className={styles.chips} role="group" aria-label={label}>
      <span className={styles.chipsLabel}>{label}</span>
      {options.map((option) => {
        const on = selected.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={on}
            className={[
              styles.chip,
              on ? styles.chipOn : "",
              option.cautioned === true ? styles.chipNonprod : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => onToggle(option.value)}
          >
            <span className={styles.chipMark} aria-hidden="true">
              {on ? "✓" : "＋"}
            </span>
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
